<template>
  <DrawerRoot
    :open="open"
    @update:open="$emit('update:open', $event)"
    :class="cn(containerVariants({ variant, size }))"
  >
    <DrawerTrigger
      v-if="$slots.default"
      as="button"
      :class="props.class"
      :data-id="trackingId"
    >
      <slot />
    </DrawerTrigger>
    <DrawerContent
      :variant="variant"
      :background="background"
      :size="size"
      side="bottom"
      :teleport="teleport"
      :dismissible="dismissible"
      :ariaProps="{
        'aria-describedby': description ? 'dialog-description' : undefined,
      }"
    >
      <template #header>
        <Primitive :class="{ 'border-b': title }" asChild>
          <slot name="header">
            <DrawerTop v-if="!title && !withSearch" />
            <DrawerHeader
              v-else
              ref="header"
              :title="title"
              :description="description"
              :withSearch="withSearch"
            >
              <template #tabs>
                <slot name="tabs" />
              </template>
              <template #search="{ query }">
                <slot name="search" :query="query" />
              </template>
            </DrawerHeader>
          </slot>
        </Primitive>
      </template>
      <slot name="content" />
      <template v-if="showFooter" #footer>
        <slot name="footer">
          <DrawerFooter
            :cancelLabel="cancelLabel"
            :confirmLabel="confirmLabel"
            :showCancel="showCancel"
            :showConfirm="showConfirm"
            :confirmType="confirmType"
            :disableConfirm="disableConfirm"
            :closeOnCancel="closeOnCancel"
            :closeOnConfirm="closeOnConfirm"
            :alignButtons="alignButtons"
            @cancel="$emit('cancel')"
            @confirm="$emit('confirm')"
          />
        </slot>
      </template>
    </DrawerContent>
  </DrawerRoot>
</template>

<script setup>
import { computed, ref } from "vue";
import { Primitive } from "radix-vue";
import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTop,
  DrawerTrigger,
} from "@/SharedComponents/Drawer";
import { cn } from "@/Utils/cn";
import { containerVariants } from ".";

defineEmits(["update:open", "cancel", "confirm"]);

const header = ref(null);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  variant: { type: String, required: false },
  background: { type: String, required: false },
  theme: { type: String, required: false },
  size: { type: [String, Number], required: false },
  icon: { type: Object, required: false },
  title: { type: String, required: false },
  description: { type: String, required: false },
  dismissible: { type: Boolean, default: true },
  teleport: { type: Boolean, default: true },
  withSearch: { type: Boolean, required: false },
  showFooter: { type: Boolean, default: false },
  cancelLabel: { type: String, required: false },
  confirmLabel: { type: String, required: false },
  showCancel: { type: Boolean, default: true },
  showConfirm: { type: Boolean, default: true },
  disableConfirm: { type: Boolean, default: false },
  closeOnCancel: { type: Boolean, default: true },
  closeOnConfirm: { type: Boolean, default: true },
  alignItems: { type: String, required: false },
  alignButtons: { type: String, required: false },
  class: { type: null, required: false },
  trackingId: { type: String, required: false },
});

const confirmType = computed(() => {
  switch (props.theme) {
    case "error":
      return "destructive";
    case "success":
      return "success";
    default:
      return undefined;
  }
});
defineExpose({
  query: computed(() => header.value?.query),
  clearQuery: () => header.value?.clearQuery(),
});
</script>
